import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Cascader } from 'antd';
import type { CascaderOption, InputNode } from '../types';
import { CASCADER_DEFAULT_OPTIONS } from './cascader-data';
import { RequiredMark } from './RequiredMark';
import { RTE_SELECT_PLACEMENTS, rteSelectPopupContainer } from '../utils/selectPlacements';

type Props = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  error?: string;
};

const parseValue = (v: string | undefined): string[] => (v ? v.split('-') : []);

const findLabelsByValues = (
  options: CascaderOption[],
  values: string[],
): string[] => {
  const labels: string[] = [];
  let cursor: CascaderOption[] | undefined = options;
  for (const segVal of values) {
    const found = cursor?.find((o) => String(o.value) === segVal);
    if (!found) break;
    labels.push(found.label as string);
    cursor = found.children as CascaderOption[] | undefined;
  }
  return labels;
};

const findOptionByValues = (
  options: CascaderOption[],
  values: string[],
): CascaderOption | undefined => {
  let currentOptions: CascaderOption[] | undefined = options;
  let selectedOption: CascaderOption | undefined;
  for (const selectedValue of values) {
    selectedOption = currentOptions?.find(
      (option) => String(option.value) === selectedValue,
    );
    if (!selectedOption) return undefined;
    currentOptions = selectedOption.children;
  }
  return selectedOption;
};

const stripOptionTitles = (options: CascaderOption[]): CascaderOption[] =>
  options.map((option) => ({
    ...option,
    title: '',
    children: option.children ? stripOptionTitles(option.children) : undefined,
  }));

/**
 * V1 parity (RichTextEditor/nodes/cascader/CascaderPortal.tsx):
 * - 动态宽度（canvas 测文本）
 * - allowClear / showSearch
 * - displayRender: `${l1}-${l2}` 紧凑显示
 * - 只点一级不提交，关闭下拉则回滚到上次合法值
 * - data-has-value 属性供 CSS 切换 arrow / clear 互斥
 *
 * Value contract (V1 compatibility):
 * - dash-separated string: `${level1}-${level2}-${level3}`
 * - 注意：option value 含 '-' 会破坏 round-trip（V1 同病）
 */
export const CascaderNode: React.FC<Props> = React.memo(
  ({ node, value, onChange, disabled, error }) => {
    const options: CascaderOption[] =
      node.cascaderOptions && node.cascaderOptions.length > 0
        ? stripOptionTitles(node.cascaderOptions)
        : stripOptionTitles(CASCADER_DEFAULT_OPTIONS);

    const placeholder = node.placeholder || '请选择省市区';

    // V1 parity（cascader/render.ts:7：variableValues[key] || node.defaultValue）：
    // value 为空时回显 node.defaultValue。用 ?? 而非 V1 的 ||——受控组件里清空得到 ''，
    // ?? 不会把 '' 再回退到 default（避免清不掉的 bug），与 SelectNode/SearchableSelectNode 的
    // `value ?? node.defaultValue` 写法统一。
    const effectiveValue = value ?? node.defaultValue;

    const [staged, setStaged] = useState<string[]>(() => parseValue(effectiveValue));
    const [searchText, setSearchText] = useState('');
    const [resetKey, setResetKey] = useState(0);
    const [width, setWidth] = useState<number>(100);
    const wrapperRef = useRef<HTMLSpanElement | null>(null);

    // 外部 value / defaultValue 同步到内部 staged
    useEffect(() => {
      setStaged(parseValue(effectiveValue));
    }, [effectiveValue]);

    const hasValue = staged.length >= 2;

    const blurSearchAfterSelection = useCallback(() => {
      window.requestAnimationFrame(() => {
        setSearchText('');
        window.requestAnimationFrame(() => {
          const input = wrapperRef.current?.querySelector<HTMLInputElement>(
            '.ant-select-selection-search-input',
          );
          input?.blur();
        });
      });
    }, []);

    const commit = useCallback(
      (next: string[]) => {
        setStaged(next);
        blurSearchAfterSelection();
        onChange(node.key!, next.join('-'));
      },
      [blurSearchAfterSelection, node.key, onChange],
    );

    const handleChange = useCallback(
      (v: (string | number)[] | null) => {
        if (!v || v.length === 0) {
          commit([]);
          return;
        }
        const next = v.map(String);
        const selectedOption = findOptionByValues(options, next);
        // 中间节点只负责展开下一级。把路径写入 searchValue 会切到搜索模式，
        // 导致刚出现的下一列立刻变成 No data。
        if (selectedOption?.children?.length) return;
        commit(next);
      },
      [commit, options],
    );

    // 下拉关闭时：若用户只点了一级，回滚到上次合法值（含 defaultValue 回退）
    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (open) {
          setSearchText('');
        } else {
          setStaged(parseValue(effectiveValue));
          setResetKey((k) => k + 1);
        }
      },
      [effectiveValue],
    );

    const getDisplayText = useCallback(() => {
      if (staged.length >= 2) {
        const labels = findLabelsByValues(options, staged);
        return labels.length ? labels.join('-') : staged.join('-');
      }
      return placeholder;
    }, [staged, options, placeholder]);

    const measureWidth = useCallback(() => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const text = getDisplayText();
      const selectorEl = wrapper.querySelector<HTMLElement>('.ant-select-selector');
      const labelEl =
        wrapper.querySelector<HTMLElement>('.ant-select-selection-item') ||
        wrapper.querySelector<HTMLElement>('.ant-select-selection-placeholder');
      const fontHost = labelEl || selectorEl || wrapper;
      const cs = window.getComputedStyle(fontHost);
      const fontSize = labelEl ? cs.fontSize : '16px';
      const fontFamily = cs.fontFamily || 'sans-serif';
      const fontWeight = cs.fontWeight || '400';
      const fontStyle = cs.fontStyle || 'normal';
      const font = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const textWidth = ctx
        ? Math.ceil(((ctx.font = font), ctx.measureText(text).width || 0))
        : 0;
      let padding = 0;
      if (selectorEl) {
        const ss = window.getComputedStyle(selectorEl);
        padding =
          (Number.parseFloat(ss.paddingLeft) || 0) +
          (Number.parseFloat(ss.paddingRight) || 0);
      }
      const arrowWidth = 16;
      const next = Math.max(80, textWidth + padding + arrowWidth + 10);
      setWidth((prev) => (prev === next ? prev : next));
    }, [getDisplayText]);

    useLayoutEffect(() => {
      const raf = window.requestAnimationFrame(() => measureWidth());
      const timer = window.setTimeout(() => measureWidth(), 60);
      return () => {
        window.cancelAnimationFrame(raf);
        window.clearTimeout(timer);
      };
    }, [measureWidth]);

    useEffect(() => {
      if (!('fonts' in document)) return;
      let cancelled = false;
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      if (!fonts?.ready) return;
      fonts.ready.then(() => {
        if (!cancelled) measureWidth();
      });
      return () => {
        cancelled = true;
      };
    }, [measureWidth]);

    return (
      <RequiredMark node={node}>
      <span
        ref={wrapperRef}
        className={['rte-cascader-host', error ? 'rte-cascader-error' : '']
          .filter(Boolean)
          .join(' ')}
        data-type="cascader"
        data-key={node.key}
        data-has-value={hasValue ? 'true' : 'false'}
        data-searching={searchText ? 'true' : 'false'}
        contentEditable={false}
        style={{ display: 'inline-block', width }}
      >
        <Cascader
          key={resetKey}
          data-type="cascader"
          data-key={node.key}
          options={options}
          value={hasValue ? staged : undefined}
          onChange={(v) => handleChange(v as (string | number)[] | null)}
          onOpenChange={handleOpenChange}
          disabled={disabled}
          placeholder={placeholder}
          changeOnSelect
          size="small"
          variant="borderless"
          allowClear
          showSearch={
            hasValue
              ? false
              : {
                  filter: (inputValue, path) =>
                    path.some((option) =>
                      (option.label as string)
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()),
                    ),
                }
          }
          searchValue={searchText}
          onSearch={setSearchText}
          popupClassName="rte-cascader-dropdown"
          placement="bottomLeft"
          builtinPlacements={RTE_SELECT_PLACEMENTS}
          getPopupContainer={rteSelectPopupContainer}
          displayRender={(labels) => labels.join('-')}
          style={{ width: '100%' }}
          suffixIcon={
            <i
              className="iconfont icon-qiehuan-xiayizhang"
              style={{ fontSize: '10px', color: '#7C3AED' }}
            />
          }
        />
      </span>
      </RequiredMark>
    );
  },
);
CascaderNode.displayName = 'CascaderNode';
